import React, {ReactNode} from 'react';
import {
    Empty,
    Space,
    Tag,
    Typography,
} from 'antd';
import {
    ClockCircleOutlined, LinkOutlined,
} from '@ant-design/icons';

import {InvocationLog} from "../../../../../../../models/logger/logger.data";

const { Text, Paragraph } = Typography;

interface EntityLinkOptions {
    entityName?: string;
    entityId?: string;
}

interface InvocationLogsTabProps {
    logs: InvocationLog[];
}

const getLevelColor = (level?: string): string => {
    const normalized = level?.toLowerCase();

    switch (normalized) {
        case 'trace':
        case 'debug':
            return 'default';

        case 'info':
            return 'blue';

        case 'notice':
        case 'success':
            return 'green';

        case 'warning':
        case 'warn':
            return 'gold';

        case 'error':
            return 'red';

        case 'critical':
        case 'fatal':
        case 'alert':
        case 'emergency':
            return 'volcano';

        default:
            return 'processing';
    }
};

const renderEntityLogLink = ({
                                 entityName,
                                 entityId,
                             }: EntityLinkOptions): ReactNode => {
    if (!entityName || !entityId) {
        return null;
    }

    return (
        <a
            href={'#'}
            className="invocation-entity-link"
            onClick={(event) => {
                event.preventDefault();
            }}
        >
            <LinkOutlined />
            <span>{entityName}</span>
            <Text type="secondary" className="invocation-entity-id">
                #{entityId}
            </Text>
        </a>
    );
};

export function InvocationLogsTab({
                                      logs
                                  }: InvocationLogsTabProps) {
    if (!logs.length) {
        return <Empty description="No logs" />;
    }

    return (
        <div className="invocation-log-list">
            {logs.map((log, index) => {
                const entityLink = renderEntityLogLink({
                    entityName: log.entityName,
                    entityId: log.entityId
                });

                return (
                    <div className="invocation-log-row" key={`${log.date}-${index}`}>
                        <div className="invocation-log-row__rail">
                            <span className="invocation-log-row__dot" />
                            {index !== logs.length - 1 ? (
                                <span className="invocation-log-row__line" />
                            ) : null}
                        </div>

                        <div className="invocation-log-row__content">
                            <div className="invocation-log-row__header">
                                <Space size={8} wrap>
                                    <ClockCircleOutlined />
                                    <Text type="secondary">{log.date}</Text>
                                    <Tag color={getLevelColor(log.level)}>
                                        {log.level || 'unknown'}
                                    </Tag>
                                </Space>

                                {entityLink ? (
                                    <div className="invocation-log-row__entity">
                                        {entityLink}
                                    </div>
                                ) : null}
                            </div>

                            <Paragraph className="invocation-log-message">
                                {log.message || '—'}
                            </Paragraph>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
